/* eslint-disable jsx-a11y/anchor-is-valid */
import dayjs from 'dayjs';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { FaWallet } from 'react-icons/fa';
import { FcAddressBook } from 'react-icons/fc';
import { calculateHash } from '../components/utils/helper';
import { db } from '../config/firebaseConfig';
import JoinRoom from './JoinRoom';
import CopyComponent from './utils/CopyComponent';

export const signTransaction = async (transaction, privateKey) => {
  // Extract first 64 chars of receiver's public key (removing 0x prefix)
  const receiverHex = transaction.receiver.replace('0x', '').substring(0, 64);
  const receiverSig = `0x${receiverHex}`;

  // Private key is already in 0x + 64 hex format
  const senderSig = privateKey;

  // Return signature with two 64-character hex values separated by space
  return `${receiverSig} ${senderSig}`;
};

const MainComponent = ({
  copyToClipboard,
  createWallet,
  userData,
  user,
  logout,
}) => {
  const [endTime, setEndTime] = useState('');
  const [roomId, setRoomId] = useState(localStorage.getItem('roomId') || '');
  const [roomName, setRoomName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [role, setRole] = useState(
    () => localStorage.getItem('role') || 'validator'
  );
  const [gameType, setGameType] = useState('ASTRAMINER');
  const [minPlayers, setMinPlayers] = useState(3);
  const [stakingDuration, setStakingDuration] = useState(2);
  const [userRooms, setUserRooms] = useState([]);
  const [loadingRooms, setLoadingRooms] = useState(false);

  // Fetch user's created and joined rooms in real-time
  useEffect(() => {
    if (!user?.email || !userData?.publicKey) return;

    setLoadingRooms(true);

    const createdRoomsQuery = db
      .collection('rooms')
      .where('createdBy', '==', user.email);

    const allRoomsQuery = db.collection('rooms');

    let createdRooms = [];
    let joinedRooms = [];

    const unsubscribeCreated = createdRoomsQuery.onSnapshot(
      (snapshot) => {
        const created = [];
        snapshot.forEach((doc) => {
          const room = {
            id: doc.id,
            ...doc.data(),
            isCreator: true,
          };
          created.push(room);
        });

        createdRooms = created;
        const merged = [...createdRooms, ...joinedRooms];
        setUserRooms(merged);
        setLoadingRooms(false);
      },
      (error) => {
        console.error('Error fetching created rooms:', error);
        toast.error('Error fetching rooms');
        setLoadingRooms(false);
      }
    );

    const unsubscribeAll = allRoomsQuery.onSnapshot(
      (snapshot) => {
        const joined = [];
        snapshot.forEach((doc) => {
          const roomData = doc.data();
          const isParticipant = roomData.participants?.includes(
            userData.publicKey
          );
          const isMiner = roomData.miners?.includes(userData.publicKey);
          const isCreator = roomData.createdBy === user.email;

          if ((isParticipant || isMiner) && !isCreator) {
            joined.push({
              id: doc.id,
              ...roomData,
              isCreator: false,
            });
          }
        });

        joinedRooms = joined;
        const merged = [...createdRooms, ...joinedRooms];
        setUserRooms(merged);
        setLoadingRooms(false);
      },
      (error) => {
        console.error('Error fetching all rooms:', error);
        setLoadingRooms(false);
      }
    );

    return () => {
      unsubscribeCreated();
      unsubscribeAll();
    };
  }, [user?.email, userData?.publicKey]);

  useEffect(() => {
    if (!roomId) return;

    const roomRef = db.collection('rooms').doc(roomId);

    const checkEndTimeAndDeleteRoom = (roomData) => {
      if (!roomData) return;

      const endTime = new Date(roomData.endTime).getTime();
      const currentTime = new Date().getTime();

      if (currentTime > endTime) {
        roomRef
          .delete()
          .then(() => {
            toast.success('Room ended due to passing the end time.');
            setRoomId('');
            localStorage.removeItem('roomId');
          })
          .catch((error) => {
            console.error('Error deleting room:', error);
            toast.error('Error deleting room.');
            localStorage.removeItem('roomId');
          });
      }
    };

    const unsubscribe = roomRef.onSnapshot(
      (roomSnapshot) => {
        if (roomSnapshot.exists) {
          const roomData = roomSnapshot.data();
          setEndTime(roomData.endTime);
          checkEndTimeAndDeleteRoom(roomData);
        }
      },
      (error) => {
        console.error('Error fetching room details:', error);
        toast.error('Error fetching room details.');
      }
    );

    return () => unsubscribe();
  }, [roomId]);

  const handleViewRoom = (roomId, roomType) => {
    const room = userRooms.find((r) => r.id === roomId);
    let userRole = 'participant';

    if (room) {
      const isMiner = room.miners?.includes(userData.publicKey);
      const isParticipant = room.participants?.includes(userData.publicKey);

      if (isMiner) {
        userRole = 'validator';
      } else if (isParticipant) {
        userRole = 'participant';
      }
    }

    localStorage.setItem('roomId', roomId);
    localStorage.setItem('role', userRole);
    const redirectUrl = roomType === 'ASTRAPOS' ? '/game' : '/joinedroom';
    window.location.href = redirectUrl;
  };

  const handleDeleteRoom = async (roomIdToDelete) => {
    if (!window.confirm('Are you sure you want to delete this room?')) {
      return;
    }

    try {
      await db.collection('rooms').doc(roomIdToDelete).delete();
      toast.success('Room deleted successfully');
    } catch (error) {
      console.error('Error deleting room:', error);
      toast.error('Failed to delete room');
    }
  };

  const handleShareRoom = (roomId) => {
    navigator.clipboard.writeText(roomId);
    toast.success('Room ID copied to clipboard!');
  };

  const handleLeaveRoom = async (roomIdToLeave) => {
    if (!window.confirm('Are you sure you want to leave this room?')) {
      return;
    }

    try {
      const roomRef = db.collection('rooms').doc(roomIdToLeave);

      await roomRef.update({
        participants: firebase.firestore.FieldValue.arrayRemove(
          userData.publicKey
        ),
        miners: firebase.firestore.FieldValue.arrayRemove(userData.publicKey),
      });

      toast.success('Left room successfully');
    } catch (error) {
      console.error('Error leaving room:', error);
      toast.error('Failed to leave room');
    }
  };

  const handleCreateRoom = async () => {
    setIsLoading(true);
    try {
      const isAstraPos = gameType === 'ASTRAPOS';

      const roomData = {
        endTime: endTime,
        createdBy: user.email,
        stakers: [],
        roomName: roomName,
        creatorAddress: userData.publicKey,
        createdAt: new Date(),
        roomType: gameType,
      };

      if (!isAstraPos) {
        roomData.participants =
          role === 'participant' ? [userData.publicKey] : [];
        roomData.miners = role === 'validator' ? [userData.publicKey] : [];
      } else {
        roomData.participants = [userData.publicKey];
        roomData.miners = [];
        roomData.astraposConfig = {
          minPlayers: minPlayers,
          stakingDuration: stakingDuration,
        };
        roomData.gameState = {
          stage: 'WAITING',
          stageStartedAt: new Date(),
          stageEndsAt: null,
          currentCycle: 1,
          blocksMinedThisCycle: 0,
        };
        roomData.readyPlayers = [];
        roomData.stakingWinner = null;
        roomData.stakingWinnerTimestamp = null;
      }

      const roomRef = await db.collection('rooms').add(roomData);

      const block = {
        sender: userData.publicKey,
        senderEmail: user.email,
        roomName: roomName,
        receiver: userData.publicKey,
        timestamp: dayjs().unix(),
        status: 'genesis',
        created_at: serverTimestamp(),
      };
      block.hash = calculateHash([], 0, block.timestamp);

      await setDoc(doc(db, 'rooms', roomRef.id, 'blocks', block.hash), block);
      setRoomId(roomRef.id);
      localStorage.setItem('roomId', roomRef.id);
      localStorage.setItem('role', isAstraPos ? 'participant' : role);
      toast.success('Room created successfully');
      window.location.href = isAstraPos ? '/game' : '/joinedroom';
    } catch (error) {
      console.error('Error creating room:', error);
      setErrorMessage('Failed to create room. Please try again later.');
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="min-h-screen pt-8 pb-12 px-6 relative overflow-hidden">
      <div className="max-w-7xl mx-auto text-white">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-white tracking-tight">
            Dashboard
          </h1>
          <div className="flex items-center gap-3 bg-[#0f111a]/60 backdrop-blur-xl rounded-xl p-1.5 border border-white/5 pr-4">
            <img
              src={`https://api.dicebear.com/6.x/avataaars/svg?seed=${userData?.publicKey || user?.email}`}
              alt="avatar"
              className="w-8 h-8 rounded-full ring-2 ring-white/5 bg-white/5"
            />
            <div className="text-right">
              <p className="text-white font-medium text-xs leading-tight">
                {userData?.email?.split('@')[0] || 'User'}
              </p>
              <div className="flex items-center gap-1.5 justify-end">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                <p className="text-white/40 text-[10px] uppercase font-bold tracking-wider">
                  Online
                </p>
              </div>
            </div>
            <button
              onClick={logout}
              className="ml-2 text-white/40 hover:text-white transition-colors"
              title="Logout"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                <polyline points="16 17 21 12 16 7"></polyline>
                <line x1="21" y1="12" x2="9" y2="12"></line>
              </svg>
            </button>
          </div>
        </div>

        {/* Stats Cards Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Balance Card */}
          <div className="bg-[#0f111a]/60 backdrop-blur-xl rounded-2xl p-6 border border-white/5 flex flex-col justify-between">
            <div className="flex items-start justify-between mb-4">
              <div className="p-3 rounded-xl text-2xl bg-purple-500/10 border border-purple-500/10">
                <FaWallet />
              </div>
              <span className="px-2.5 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-400 text-xs font-medium">
                Wallet
              </span>
            </div>
            <div>
              <p className="text-white/40 text-sm font-medium mb-1">
                Total Balance
              </p>
              <h2 className="text-3xl font-bold text-white tracking-tight">
                {userData?.balance?.toFixed(2) || '0.00'}{' '}
              </h2>
            </div>
          </div>

          {/* Public Key Card */}
          <div className="bg-[#0f111a]/60 backdrop-blur-xl rounded-2xl p-6 border border-white/5 flex flex-col justify-between group">
            <div className="flex items-start justify-between mb-4">
              <div className="p-[.6rem] rounded-xl bg-blue-500/10 border border-blue-500/10">
                <FcAddressBook className="text-4xl" />
              </div>
              <span className="px-2.5 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs font-medium">
                Public Key
              </span>
            </div>

            <div>
              <div className="flex items-center gap-2 bg-black/20 p-2 rounded-lg border border-white/5 group-hover:border-white/10 transition-colors">
                <code className="text-white/80 font-mono text-sm truncate flex-1 block">
                  {userData?.publicKey || 'Loading...'}
                </code>
                <CopyComponent data={userData?.publicKey} />
              </div>
            </div>
          </div>

          {/* Private Key Card */}
          <div className="bg-[#0f111a]/60 backdrop-blur-xl rounded-2xl p-6 border border-white/5 flex flex-col justify-between group">
            <div className="flex items-start justify-between mb-4">
              <div className="p-3 rounded-xl text-2xl bg-pink-500/10 border border-pink-500/10">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="text-blue-400"
                >
                  <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
                </svg>
              </div>
              <span className="px-2.5 py-1 rounded-full bg-pink-500/10 border border-pink-500/20 text-pink-400 text-xs font-medium">
                Private Key
              </span>
            </div>
            <div>
              {/* <p className="text-white/40 text-sm font-medium mb-2">
                Private Key
              </p> */}
              <div className="flex items-center gap-2 bg-black/20 p-2 rounded-lg border border-white/5 group-hover:border-white/10 transition-colors">
                <code className="text-white/80 font-mono text-sm truncate flex-1 block tracking-widest">
                  {userData?.privateKey ? '••••••••••••••••' : 'None'}
                </code>
                <CopyComponent data={userData?.privateKey} />
              </div>
            </div>
          </div>
        </div>

        {/* Room Management Section */}
        {!userData?.isWalletCreated && (
          <div className="bg-[#0f111a]/60 backdrop-blur-xl rounded-2xl border border-white/5 overflow-hidden">
            <div className="p-6 border-b border-white/5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white/[0.02]">
              <div>
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <span className="w-8 h-8 rounded-lg bg-orange-500/10 flex items-center justify-center text-orange-400 border border-orange-500/10">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="3" y="3" width="7" height="7"></rect>
                      <rect x="14" y="3" width="7" height="7"></rect>
                      <rect x="14" y="14" width="7" height="7"></rect>
                      <rect x="3" y="14" width="7" height="7"></rect>
                    </svg>
                  </span>
                  Room Management
                </h3>
                <p className="text-white/40 text-sm mt-1 ml-10">
                  View statuses and manage your active sessions
                </p>
              </div>
              {userRooms.length > 0 && (
                <span className="bg-white/5 text-white/60 px-3 py-1 rounded-full text-xs font-medium border border-white/5">
                  {userRooms.length} Total Rooms
                </span>
              )}
            </div>

            {!userData?.isWalletCreated ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white/5 flex items-center justify-center text-white/20">
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  >
                    <rect x="2" y="4" width="20" height="16" rx="2" />
                    <path d="M7 15h0M2 10h20" />
                    <path d="M7 15h0v0h0v0Z" />
                  </svg>
                </div>
                <p className="text-white/50 text-sm mb-4">
                  Create a wallet to access rooms and blockchain features
                </p>
                <button
                  onClick={createWallet}
                  className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-500 rounded-lg font-medium text-white transition-colors text-sm"
                >
                  Create Wallet
                </button>
              </div>
            ) : loadingRooms ? (
              <div className="text-center py-16">
                <div className="w-8 h-8 mx-auto border-2 border-white/10 border-t-indigo-500 rounded-full animate-spin mb-3"></div>
                <p className="text-white/50 text-sm">Loading rooms...</p>
              </div>
            ) : userRooms.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white/5 flex items-center justify-center text-white/20">
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242" />
                    <path d="M12 12v9" />
                    <path d="m16 16-4-4-4 4" />
                  </svg>
                </div>
                <p className="text-white font-medium mb-1">
                  No rooms created yet
                </p>
                <p className="text-white/40 text-sm">
                  Create or join a room above to get started
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/5 bg-white/[0.01]">
                      <th className="text-left py-4 px-6 text-white/40 font-medium text-xs uppercase tracking-wider">
                        Room Name
                      </th>
                      <th className="text-left py-4 px-6 text-white/40 font-medium text-xs uppercase tracking-wider">
                        Room ID
                      </th>
                      <th className="text-left py-4 px-6 text-white/40 font-medium text-xs uppercase tracking-wider">
                        Type
                      </th>
                      <th className="text-left py-4 px-6 text-white/40 font-medium text-xs uppercase tracking-wider">
                        Status
                      </th>
                      <th className="text-left py-4 px-6 text-white/40 font-medium text-xs uppercase tracking-wider">
                        Players
                      </th>
                      <th className="text-left py-4 px-6 text-white/40 font-medium text-xs uppercase tracking-wider">
                        End Time
                      </th>
                      <th className="text-right py-4 px-6 text-white/40 font-medium text-xs uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {userRooms.map((room) => (
                      <tr
                        key={room.id}
                        className="hover:bg-white/[0.02] transition-colors"
                      >
                        <td className="py-4 px-6">
                          <p className="text-white font-medium text-sm">
                            {room.roomName}
                          </p>
                        </td>
                        <td className="py-4 px-6">
                          <div
                            className="flex items-center gap-2 group cursor-pointer"
                            onClick={() => handleShareRoom(room.id)}
                          >
                            <code className="text-white/60 text-xs bg-black/20 px-2 py-1 rounded font-mono border border-white/5 group-hover:border-white/20 transition-colors">
                              {room.id.substring(0, 8)}...
                            </code>
                            <svg
                              className="w-3.5 h-3.5 text-white/20 group-hover:text-white/60 transition-colors"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <rect
                                x="9"
                                y="9"
                                width="13"
                                height="13"
                                rx="2"
                                ry="2"
                              ></rect>
                              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                            </svg>
                          </div>
                        </td>
                        <td className="py-4 px-6">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                              room.roomType === 'ASTRAPOS'
                                ? 'bg-purple-500/10 text-purple-400 border-purple-500/10'
                                : 'bg-blue-500/10 text-blue-400 border-blue-500/10'
                            }`}
                          >
                            {room.roomType || 'ASTRAMINER'}
                          </span>
                        </td>
                        <td className="py-4 px-6">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                              room.isCreator
                                ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/10'
                                : 'bg-cyan-500/10 text-cyan-400 border-cyan-500/10'
                            }`}
                          >
                            {room.isCreator ? 'Owner' : 'Participant'}
                          </span>
                        </td>
                        <td className="py-4 px-6">
                          <div className="flex items-center gap-1.5 text-white/60">
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                              <circle cx="9" cy="7" r="4"></circle>
                              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                            </svg>
                            <span className="text-sm">
                              {(room.participants?.length || 0) +
                                (room.miners?.length || 0)}
                            </span>
                          </div>
                        </td>
                        <td className="py-4 px-6">
                          <p className="text-white/60 text-xs">
                            {room.endTime
                              ? new Date(room.endTime).toLocaleDateString()
                              : '--'}
                          </p>
                        </td>
                        <td className="py-4 px-6 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() =>
                                handleViewRoom(room.id, room.roomType)
                              }
                              className="p-2 bg-white/5 hover:bg-white/10 text-white/80 rounded-lg transition-colors border border-white/5"
                              title="View Room"
                            >
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                                <polyline points="15 3 21 3 21 9"></polyline>
                                <line x1="10" y1="14" x2="21" y2="3"></line>
                              </svg>
                            </button>
                            {room.isCreator ? (
                              <button
                                onClick={() => handleDeleteRoom(room.id)}
                                className="p-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors border border-red-500/10"
                                title="Delete Room"
                              >
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <path d="M3 6h18"></path>
                                  <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                                  <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                                </svg>
                              </button>
                            ) : (
                              <button
                                onClick={() => handleLeaveRoom(room.id)}
                                className="p-2 bg-orange-500/10 hover:bg-orange-500/20 text-orange-400 rounded-lg transition-colors border border-orange-500/10"
                                title="Leave Room"
                              >
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                                  <polyline points="16 17 21 12 16 7"></polyline>
                                  <line x1="21" y1="12" x2="9" y2="12"></line>
                                </svg>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Action Grid: Create & Join */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8 pt-3">
          {/* Create Room Card */}
          <div className="bg-[#0f111a]/60 backdrop-blur-xl rounded-2xl border border-white/5 overflow-hidden flex flex-col">
            <div className="p-6 border-b border-white/5 bg-white/[0.02]">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-400 border border-emerald-500/10">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                  </svg>
                </span>
                Create a Room
              </h3>
            </div>
            <div className="p-6 space-y-5">
              <div className="space-y-4">
                <div>
                  <label className="block text-white/40 text-xs font-medium uppercase tracking-wider mb-1.5">
                    Room Name
                  </label>
                  <input
                    type="text"
                    className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all font-medium"
                    placeholder="e.g. Satoshi's Lounge"
                    value={roomName}
                    onChange={(e) => setRoomName(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-white/40 text-xs font-medium uppercase tracking-wider mb-1.5">
                    End Time
                  </label>
                  <input
                    type="datetime-local"
                    className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all font-medium [color-scheme:dark]"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white/40 text-xs font-medium uppercase tracking-wider mb-1.5">
                      Game Type
                    </label>
                    <div className="relative">
                      <select
                        className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-indigo-500 transition-all appearance-none"
                        value={gameType}
                        onChange={(e) => setGameType(e.target.value)}
                      >
                        <option value="ASTRAMINER">AstraMiner</option>
                        <option value="ASTRAPOS">AstraPOS</option>
                      </select>
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-white/40">
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M6 9l6 6 6-6" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {gameType === 'ASTRAMINER' ? (
                    <div>
                      <label className="block text-white/40 text-xs font-medium uppercase tracking-wider mb-1.5">
                        Your Role
                      </label>
                      <div className="relative">
                        <select
                          className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-indigo-500 transition-all appearance-none"
                          value={role}
                          onChange={(e) => setRole(e.target.value)}
                        >
                          <option value="validator">Validator</option>
                          <option value="participant">Participant</option>
                        </select>
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-white/40">
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M6 9l6 6 6-6" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <label className="block text-white/40 text-xs font-medium uppercase tracking-wider mb-1.5">
                        Players
                      </label>
                      <input
                        type="number"
                        min="2"
                        max="10"
                        value={minPlayers}
                        onChange={(e) =>
                          setMinPlayers(
                            Math.max(2, parseInt(e.target.value) || 2)
                          )
                        }
                        className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-indigo-500 transition-all"
                      />
                    </div>
                  )}
                </div>
                {gameType === 'ASTRAPOS' && (
                  <div>
                    <label className="block text-white/40 text-xs font-medium uppercase tracking-wider mb-1.5">
                      Staking Duration (Min)
                    </label>
                    <input
                      type="number"
                      min="2"
                      max="30"
                      value={stakingDuration}
                      onChange={(e) =>
                        setStakingDuration(
                          Math.max(2, parseInt(e.target.value) || 2)
                        )
                      }
                      className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-indigo-500 transition-all"
                    />
                  </div>
                )}
              </div>
              <button
                disabled={
                  isLoading ||
                  !endTime ||
                  !roomName ||
                  !userData?.isWalletCreated
                }
                onClick={handleCreateRoom}
                className="w-full px-6 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 rounded-xl font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-indigo-500/20 active:scale-[0.98]"
              >
                {isLoading ? 'Creating...' : 'Create Room'}
              </button>
            </div>
          </div>

          {/* Join Room Card */}
          <div className="bg-[#0f111a]/60 backdrop-blur-xl rounded-2xl border border-white/5 overflow-hidden flex flex-col h-full">
            <div className="p-6 border-b border-white/5 bg-white/[0.02]">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-indigo-500/10 flex items-center justify-center text-indigo-400 border border-indigo-500/10">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                    <polyline points="10 17 15 12 10 7" />
                    <line x1="15" y1="12" x2="3" y2="12" />
                  </svg>
                </span>
                Join a Room
              </h3>
            </div>
            <div className="p-6 flex-1 flex flex-col">
              <div className="flex-1">
                <p className="text-white/50 text-sm mb-6 leading-relaxed">
                  Enter an existing Room ID to join the session. Ensure you have
                  the correct permissions and your wallet is connected.
                </p>
                <div className="relative z-10">
                  <JoinRoom />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MainComponent;
